import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import logger from '@/util/logger';
import type { ImageOption } from '@rock/types';
import { Loader2 } from 'lucide-react';
import { type FC, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { searchTestLibraryImages } from './testLibraryApi';

const log = logger('ImageSearchDialog');

/**
 * Pick a photo from a Google Images search.
 *
 * The query is editable and the search only runs on the button: each run costs a SerpAPI credit, and
 * nameplate phrasings in particular often need a retype (Google drifts toward engraved-sign
 * products). Results are shown at a size where you can actually tell whether a plate is legible —
 * a thumbnail grid is useless for judging that.
 */
export const ImageSearchDialog: FC<{
    open: boolean;
    onOpenChange: (open: boolean) => void;
    title: string;
    defaultQuery: string;
    onSelect: (image: ImageOption) => void;
}> = ({ open, onOpenChange, title, defaultQuery, onSelect }) => {
    const [query, setQuery] = useState(defaultQuery);
    const [results, setResults] = useState<ImageOption[] | undefined>();
    const [isSearching, setIsSearching] = useState(false);

    // Reset to the caller's query each time it opens — the brand/type it was composed from may have
    // changed since the last search.
    useEffect(() => {
        if (open) {
            setQuery(defaultQuery);
            setResults(undefined);
        }
    }, [open, defaultQuery]);

    const runSearch = async () => {
        const trimmed = query.trim();
        if (!trimmed) return;
        setIsSearching(true);
        try {
            const options = await searchTestLibraryImages(trimmed);
            setResults(options);
            if (options.length === 0) {
                toast.info('No images found', { description: 'Try different search terms.' });
            }
        } catch (err) {
            log.error('Image search failed', { err });
            toast.error('Search failed', {
                description: err instanceof Error ? err.message : 'An unexpected error occurred.',
            });
        } finally {
            setIsSearching(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        Each search spends a SerpAPI credit. Pick the clearest photo — for a nameplate, one where the
                        model and serial are both readable.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-2">
                    <Label htmlFor="image-search-query">Search</Label>
                    <div className="flex gap-2">
                        <Input
                            id="image-search-query"
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') void runSearch();
                            }}
                            disabled={isSearching}
                        />
                        <Button type="button" onClick={() => void runSearch()} disabled={isSearching || !query.trim()}>
                            {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
                        </Button>
                    </div>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                    {results === undefined && !isSearching && (
                        <div className="py-8 text-center text-sm text-muted-foreground">
                            Edit the search terms if needed, then search.
                        </div>
                    )}
                    {results && results.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                            {results.map((image) => (
                                <button
                                    key={image.id}
                                    type="button"
                                    className="group overflow-hidden rounded border bg-slate-50 p-1 hover:border-primary"
                                    onClick={() => {
                                        onSelect(image);
                                        onOpenChange(false);
                                    }}
                                >
                                    <img
                                        src={image.url}
                                        alt={image.title ?? 'Search result'}
                                        className="h-40 w-full object-contain"
                                        loading="lazy"
                                    />
                                    <div className="truncate pt-1 text-xs text-muted-foreground">
                                        {image.width}×{image.height}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
